import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { getAnchoredPaperSpacing } from '@components/v2/ui/utils/anchoredPaperSpacing';
import { Divider, Stack } from '@mui/material';
import { ChevronDown, Plus } from 'lucide-react';
import { MouseEvent, useState } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { AddOrderProductTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { AddOrderProductMode } from '../../../../products/orderProducts.types';
import AskRachelAboutModesButton from './AskRachelAboutModesButton';
import { useAddProductToggleButtonStyles } from './addProductToggleButton.styles';
import { AddProductToggleButtonProps, MODE_OPTIONS } from './addProductToggleButton.types';

const AddProductToggleButton = ({
  isOpen,
  mode,
  isModeSelectable = true,
  isCompact,
  onToggleOpen,
  onSelectMode,
}: AddProductToggleButtonProps) => {
  const { translate } = useTranslation();
  const { classes } = useAddProductToggleButtonStyles({ isOpen });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectMode = (nextMode: AddOrderProductMode) => {
    onSelectMode(nextMode);
    handleCloseMenu();
  };

  const selectedModeOption = MODE_OPTIONS.find((option) => option.mode === mode);
  const toggleIcon = selectedModeOption?.icon ?? Plus;

  const toggleControl = isCompact ? (
    <IconButton
      icon={toggleIcon}
      color="secondary"
      isActive={isOpen}
      testId={AddOrderProductTestIds.ADD_CODE_BUTTON}
      ariaLabel={translate(LangEnum.GLOBAL_ADD)}
      onClick={onToggleOpen}
    />
  ) : (
    <Button
      variant="text"
      color="secondary"
      shape="square"
      isActive={isOpen}
      testId={AddOrderProductTestIds.ADD_CODE_BUTTON}
      startIcon={<Icon icon={toggleIcon} size="sm" />}
      onClick={onToggleOpen}
    >
      {translate(LangEnum.GLOBAL_ADD)}
    </Button>
  );

  return (
    <Stack direction="row" className={classes.toggleGroup}>
      {toggleControl}

      {isModeSelectable && (
        <>
          <Divider orientation="vertical" flexItem />

          <IconButton
            icon={ChevronDown}
            color="primary"
            testId={AddOrderProductTestIds.MODE_MENU_BUTTON}
            ariaLabel={translate(LangEnum.GLOBAL_ADD)}
            onClick={handleOpenMenu}
          />

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: getAnchoredPaperSpacing({ variant: 'dropdown' }) } }}
          >
            <ListSubheader sx={{ pt: 0.5 }} testId={AddOrderProductTestIds.MODE_MENU_TITLE}>
              <Stack
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
              >
                {translate(LangEnum.ADD_ORDER_PRODUCTS_MODE_MENU_TITLE)}

                <AskRachelAboutModesButton onAsk={handleCloseMenu} />
              </Stack>
            </ListSubheader>

            {MODE_OPTIONS.map((option) => (
              <MenuItem
                key={option.mode}
                selected={option.mode === mode}
                testId={option.testId}
                onClick={() => handleSelectMode(option.mode)}
              >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                  <Icon icon={option.icon} size="sm" />
                  <Stack sx={{ gap: 0.25 }}>
                    <Typography variant="body2">{translate(option.label)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {translate(option.description)}
                    </Typography>
                  </Stack>
                </Stack>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
};

export default AddProductToggleButton;
